import { useEffect, useState } from 'react';

import type {
  SeekingStatus,
  VitaSeekingReason,
} from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import type { VitaPreferencesFragment } from '@xing-com/crate-lebenslauf-graphql';
import {
  useVitaReactiveVariableActions,
  useVitaTracking,
} from '@xing-com/crate-lebenslauf-hooks';
import type { PreferencesProps } from '@xing-com/crate-lebenslauf-xinglets';
import { PopUpWindow } from '@xing-com/pop-up-window';

import { StyledScrollWrapper } from './preferences.styled';
import { JobPreferences } from './screens/job-preferences';
import { JobStatus } from './screens/job-status';
import { Reason } from './screens/reason';

export type OnSaveProps = VitaPreferencesFragment & {
  otherSeekingReason?: string;
};

const seekingStatusTrackingMap = {
  SEEKING: 'actively_looking',
  INTERESTED: 'passive_looking',
};

const mapSeekingStatusToTrackingString = (
  seekingReason: VitaSeekingReason | undefined | null
): string => {
  if (seekingReason === 'ALREADY_HAVE_JOB_TO_APPLY')
    return seekingReason?.toLocaleLowerCase();
  else if (seekingReason === 'JUST_TEST_CV_EDITOR') return 'create_cv_only';
  else if (seekingReason === 'KEEP_CV_UP_TO_DATE') return 'future_reference';
  else return 'other';
};

export const Preferences: React.FC<PreferencesProps> = ({
  togglePreferences,
  show,
}) => {
  const [seekingStatus, setSeekingStatus] = useState<SeekingStatus>();
  const { isLoggedOut } = useLoginState();
  const { trackWithNWT } = useVitaTracking({
    pageName: 'editor',
  });
  const { cvActions } = useVitaReactiveVariableActions();
  const handleOnSave = ({
    locations,
    targetPosition,
    homeOffice,
    seekingReason,
    otherSeekingReason,
  }: OnSaveProps): void => {
    const preferences: string[] = [];
    const preferencesOptions: string[] = [];

    cvActions({
      type: 'add-preferences',
      value: {
        seekingReason,
        seekingStatus,
        targetPosition,
        homeOffice: homeOffice || false,
        locations: locations || [],
        otherSeekingReason: otherSeekingReason || '',
      },
    });
    togglePreferences();
    globalThis.window?.localStorage.setItem('vita-preferences', 'true');

    if (locations && locations.length > 0) {
      preferences.push('preferred_location');
      preferencesOptions.push('city_updated');
    }
    if (targetPosition) {
      preferences.push('ideal_positions');
      preferencesOptions.push('job_title_updated');
    }
    if (homeOffice) {
      preferences.push('home_office');
      preferencesOptions.push('home_office_preference_updated');
    }

    if (seekingStatus) {
      trackWithNWT({
        event: 'submitted',
        element: 'jobseeker_status',
        elementDetail:
          seekingStatus === 'NOT_SEEKING'
            ? `not_interested_reason_${mapSeekingStatusToTrackingString(seekingReason)}`
            : seekingStatusTrackingMap[seekingStatus],
      });
    }

    if (preferencesOptions.length > 0) {
      setTimeout(() => {
        trackWithNWT({
          event: 'submitted',
          element: 'preferences_jobseeker_criteria',
          elementDetail: preferences.join(','),
          context: preferencesOptions.join(','),
        });
      }, 100);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      const vitaPreferencesSet =
        globalThis.window?.localStorage.getItem('vita-preferences') === 'true';
      if (isLoggedOut && !vitaPreferencesSet) {
        togglePreferences();
      }
    }, 5000);
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [isLoggedOut]);

  useEffect(() => {
    if (!show) setSeekingStatus(undefined);
  }, [show]);

  return (
    <PopUpWindow isShown={show}>
      <StyledScrollWrapper>
        {!seekingStatus && <JobStatus onJobSearchSelect={setSeekingStatus} />}
        {(seekingStatus === 'SEEKING' || seekingStatus === 'INTERESTED') && (
          <JobPreferences
            onSave={handleOnSave}
            onBack={() => setSeekingStatus(undefined)}
          />
        )}
        {seekingStatus === 'NOT_SEEKING' && (
          <Reason
            onSave={handleOnSave}
            onBack={() => setSeekingStatus(undefined)}
          />
        )}
      </StyledScrollWrapper>
    </PopUpWindow>
  );
};
